/**
 * Versioning API — branches, commits, diff, merge.
 */

import { apiClient } from "./client";
import { supabase } from "../lib/supabase";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

// -- Types --

export interface Branch {
  id: string;
  name: string;
  parent_branch: string | null;
  fork_commit: string | null;
  created_at: string;
  created_by: string;
}

export interface Commit {
  id: string;
  branch: string;
  parent_ids: string[] | null;
  message: string;
  author: string;
  snapshot_hash: string;
  created_at: string;
}

export interface DiffEntry {
  path: string;
  status: "only_a" | "only_b" | "modified" | "unchanged";
  value_a_json: unknown;
  value_b_json: unknown;
}

export interface MergePreviewEntry {
  path: string;
  action: "conflict" | "added" | "modified" | "deleted" | "target_only" | "no_action";
  conflict: boolean;
  source_value: unknown;
  target_value: unknown;
  base_value: unknown;
}

export interface MergeResult {
  merged_count: number;
  conflict_count: number;
  conflicts: Array<{ path: string; source_value: unknown; target_value: unknown }>;
}

export interface Tag {
  id: string;
  name: string;
  commit_id: string;
  branch: string;
  message: string | null;
  author: string;
  created_at: string;
  commit_message?: string;
  snapshot_hash?: string;
}

export interface SquashResult {
  status: "squashed" | "nothing_to_squash";
  commit_id?: string;
}

export interface UncommittedSummary {
  total: number;
  inserts: number;
  updates: number;
  deletes: number;
}

// -- Branch API --

export function fetchBranches(projectId: string): Promise<Branch[]> {
  return apiClient<Branch[]>(`/versioning/branches?project_id=${projectId}`);
}

export function createBranch(
  projectId: string,
  name: string,
  parentBranch = "main",
): Promise<Branch> {
  return apiClient<Branch>("/versioning/branches", {
    method: "POST",
    body: { project_id: projectId, name, parent_branch: parentBranch },
  });
}

export function deleteBranch(
  projectId: string,
  branchName: string,
): Promise<void> {
  return apiClient<void>(
    `/versioning/branches?project_id=${projectId}&branch=${encodeURIComponent(branchName)}`,
    { method: "DELETE" },
  );
}

export function renameBranch(
  projectId: string,
  oldName: string,
  newName: string,
): Promise<{ status: string; old_name: string; new_name: string }> {
  return apiClient("/versioning/branches", {
    method: "PATCH",
    body: { project_id: projectId, old_name: oldName, new_name: newName },
  });
}

export function moveNodes(
  projectId: string,
  nodeIds: string[],
  targetBranch: string,
): Promise<{ status: string; moved_count: number }> {
  return apiClient("/versioning/move-nodes", {
    method: "POST",
    body: { project_id: projectId, node_ids: nodeIds, target_branch: targetBranch },
  });
}

// -- Commit API --

export function fetchCommits(
  projectId: string,
  branch: string,
  limit = 50,
): Promise<Commit[]> {
  return apiClient<Commit[]>(
    `/versioning/commits?project_id=${projectId}&branch=${encodeURIComponent(branch)}&limit=${limit}`,
  );
}

export function createCommit(
  projectId: string,
  branch: string,
  message: string,
): Promise<{ commit_id: string }> {
  return apiClient<{ commit_id: string }>("/versioning/commits", {
    method: "POST",
    body: { project_id: projectId, branch, message },
  });
}

// -- Uncommitted changes --

export function fetchUncommitted(
  projectId: string,
  branch: string,
): Promise<UncommittedSummary> {
  return apiClient<UncommittedSummary>(
    `/versioning/uncommitted?project_id=${projectId}&branch=${encodeURIComponent(branch)}`,
  );
}

// -- Diff API --

export function diffBranches(
  projectId: string,
  branchA: string,
  branchB: string,
): Promise<DiffEntry[]> {
  return apiClient<DiffEntry[]>("/versioning/diff", {
    method: "POST",
    body: { project_id: projectId, branch_a: branchA, branch_b: branchB },
  });
}

// -- Merge API --

export function mergePreview(
  projectId: string,
  sourceBranch: string,
  targetBranch: string,
): Promise<MergePreviewEntry[]> {
  return apiClient<MergePreviewEntry[]>("/versioning/merge/preview", {
    method: "POST",
    body: { project_id: projectId, source_branch: sourceBranch, target_branch: targetBranch },
  });
}

export class ApprovalsRequiredError extends Error {
  constructor(
    public status: "approvals_required" | "approvals_rejected",
    public pendingApprovals: unknown[],
  ) {
    super("Merge requires owner approvals");
    this.name = "ApprovalsRequiredError";
  }
}

export async function applyMerge(
  projectId: string,
  sourceBranch: string,
  targetBranch: string,
  force = false,
): Promise<MergeResult> {
  const token = await getAuthToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  } else if (API_BASE.startsWith("/api") || API_BASE.includes("localhost")) {
    headers["X-User-Id"] = "local-dev";
    headers["X-User-Name"] = "Local Developer";
  }

  const res = await fetch(`${API_BASE}/versioning/merge/apply`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      project_id: projectId,
      source_branch: sourceBranch,
      target_branch: targetBranch,
      force,
    }),
  });

  if (res.status === 409) {
    const body = await res.json();
    throw new ApprovalsRequiredError(
      body.status ?? "approvals_required",
      body.pending_approvals ?? body.rejected ?? [],
    );
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "Unknown error");
    throw new Error(`API ${res.status}: ${text}`);
  }

  return res.json() as Promise<MergeResult>;
}

// -- Merge with per-path resolutions --

export function applyMergeResolved(
  projectId: string,
  sourceBranch: string,
  targetBranch: string,
  resolutions: Record<string, "source" | "target">,
): Promise<MergeResult> {
  return apiClient<MergeResult>("/versioning/merge/resolve", {
    method: "POST",
    body: {
      project_id: projectId,
      source_branch: sourceBranch,
      target_branch: targetBranch,
      resolutions,
    },
  });
}

// -- Tag API --

export function fetchTags(projectId: string): Promise<Tag[]> {
  return apiClient<Tag[]>(`/versioning/tags?project_id=${projectId}`);
}

export function createTag(
  projectId: string,
  name: string,
  commitId: string,
  branch: string,
  message?: string,
): Promise<Tag> {
  return apiClient<Tag>("/versioning/tags", {
    method: "POST",
    body: { project_id: projectId, name, commit_id: commitId, branch, message },
  });
}

export function deleteTag(
  projectId: string,
  name: string,
): Promise<void> {
  return apiClient<void>(
    `/versioning/tags?project_id=${projectId}&name=${encodeURIComponent(name)}`,
    { method: "DELETE" },
  );
}

// -- Squash API --

export function squashCommits(
  projectId: string,
  branch: string,
  message: string,
  keepLast = 0,
): Promise<SquashResult> {
  return apiClient<SquashResult>("/versioning/squash", {
    method: "POST",
    body: { project_id: projectId, branch, message, keep_last: keepLast },
  });
}
